import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, ChevronLeft, FileText, Shield, AlertTriangle, List, MessageSquare, HelpCircle, Save, Loader2, Download, ClipboardList, FolderLock } from "lucide-react";
import axios from "axios";
import SaveToCaseLockerModal from "../components/SaveToCaseLockerModal";
import LinkedCaseChip from "../components/LinkedCaseChip";

const API = process.env.REACT_APP_BACKEND_URL;

/* ─── Reusable ResultBlock ─── */
function ResultBlock({ title, icon: Icon, children, accentColor }) {
  return (
    <div className="ff-result-block" data-testid={`result-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="ff-rb-header">
        {Icon && <Icon size={16} style={{ color: accentColor || "var(--color-accent)" }} />}
        <h3>{title}</h3>
      </div>
      <div className="ff-rb-body">{children}</div>
    </div>
  );
}

/* ─── Step indicator ─── */
const STEPS = [
  { id: 1, label: "Who" },
  { id: 2, label: "What" },
  { id: 3, label: "Details" },
  { id: 4, label: "Context" },
  { id: 5, label: "Review" },
];

function StepBar({ current }) {
  return (
    <div className="ff-step-bar" data-testid="step-bar">
      {STEPS.map((s) => (
        <div key={s.id} className={`ff-step ${current >= s.id ? "active" : ""} ${current === s.id ? "current" : ""}`}>
          <span className="ff-step-num">{s.id}</span>
          <span className="ff-step-label">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── Field component ─── */
function Field({ label, name, value, onChange, placeholder, multiline, required }) {
  const Tag = multiline ? "textarea" : "input";
  return (
    <div className="ff-field">
      <label>{label}{required && <span className="ff-req">*</span>}</label>
      <Tag
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={multiline ? 4 : undefined}
        data-testid={`field-${name}`}
      />
    </div>
  );
}

export default function FactFinderPage({ user }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    employee_name: "", department: "", location: "",
    incident_date: "", meeting_date: "", subject: "",
    what_happened: "", instruction_given: "", employee_response: "",
    supervisor_name: "", other_people: "", witnesses: "",
    documents_available: "", prior_related_events: "",
    safety_concerns: "", contract_concerns: "", notes: "",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [questionLoading, setQuestionLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [showCaseLockerModal, setShowCaseLockerModal] = useState(false);
  const [linkedRefreshKey, setLinkedRefreshKey] = useState(0);

  // Load draft from URL if present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const draftId = params.get("draft");
    if (draftId) loadDraft(draftId);
  }, []);

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function loadDraft(id) {
    try {
      const { data } = await axios.get(`${API}/api/fact-finder/drafts/${id}`);
      const d = data;
      setForm({
        employee_name: d.employee_name || "",
        department: d.department || "",
        location: d.location || "",
        incident_date: d.incident_date || "",
        meeting_date: d.meeting_date || "",
        subject: d.subject || "",
        what_happened: d.what_happened || "",
        instruction_given: d.instruction_given || "",
        employee_response: d.employee_response || "",
        supervisor_name: d.supervisor_name || "",
        other_people: d.other_people || "",
        witnesses: d.witnesses || "",
        documents_available: d.documents_available || "",
        prior_related_events: d.prior_related_events || "",
        safety_concerns: d.safety_concerns || "",
        contract_concerns: d.contract_concerns || "",
        notes: d.notes || "",
      });
      if (d.analysis) {
        setResult(d.analysis);
        setStep(6);
      }
      setCurrentDraftId(id);
    } catch { /* silent */ }
  }

  async function runAnalysis() {
    setLoading(true);
    setSaveMessage("");
    try {
      const { data } = await axios.post(`${API}/api/fact-finder/analyze`, form);
      setResult(data);
      setStep(6);
    } catch (err) {
      console.error(err);
      setSaveMessage("Analysis failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function generateQuestionsOnly() {
    setQuestionLoading(true);
    setSaveMessage("");
    try {
      const { data } = await axios.post(`${API}/api/fact-finder/questions`, form);
      setResult((prev) => ({
        ...(prev || {}),
        likely_questions: data.likely_questions || [],
      }));
      setStep(6);
    } catch (err) {
      console.error(err);
      setSaveMessage("Unable to generate likely questions.");
    } finally {
      setQuestionLoading(false);
    }
  }

  function exportPdf() {
    if (!currentDraftId) {
      setSaveMessage("Save your draft first before exporting PDF.");
      return;
    }
    window.open(`${API}/api/fact-finder/drafts/${currentDraftId}/pdf`, "_blank");
  }

  async function saveDraft() {
    setSaveMessage("");
    try {
      const payload = { ...form, analysis: result, status: "draft" };
      if (currentDraftId) {
        const { data } = await axios.put(`${API}/api/fact-finder/drafts/${currentDraftId}`, payload);
        setSaveMessage("Draft updated successfully.");
        setCurrentDraftId(data.id);
      } else {
        const { data } = await axios.post(`${API}/api/fact-finder/save`, payload);
        setSaveMessage("Draft saved successfully.");
        setCurrentDraftId(data.id);
      }
    } catch {
      setSaveMessage("Unable to save draft.");
    }
  }

  return (
    <div className="ff-page" data-testid="fact-finder-form-page">
      <style>{cssText}</style>

      {/* Header */}
      <header className="ff-pg-header">
        <button className="ff-back" onClick={() => navigate("/fact-finder")} data-testid="back-btn">
          <ArrowLeft size={16} /> Back
        </button>
        <div>
          <span className="ff-badge">Fact Finder</span>
          <h1>Build Your Record</h1>
          <p>Walk through the facts step by step. Stay calm. Stay accurate.</p>
        </div>
      </header>

      <StepBar current={step <= 5 ? step : 5} />

      {/* Step 1: Who */}
      {step === 1 && (
        <div className="ff-card" data-testid="step-1">
          <h2>Step 1: Who Is Involved?</h2>
          <div className="ff-grid">
            <Field label="Employee Name" name="employee_name" value={form.employee_name} onChange={handleChange} placeholder="Full name" />
            <Field label="Department" name="department" value={form.department} onChange={handleChange} placeholder="e.g., Substation Maintenance" />
            <Field label="Location" name="location" value={form.location} onChange={handleChange} placeholder="e.g., Station 42" />
            <Field label="Supervisor Name" name="supervisor_name" value={form.supervisor_name} onChange={handleChange} placeholder="Who directed or questioned you?" />
            <Field label="Other People Involved" name="other_people" value={form.other_people} onChange={handleChange} placeholder="Names and roles" />
          </div>
          <div className="ff-nav">
            <div />
            <button className="ff-next" onClick={() => setStep(2)} data-testid="next-btn">Next <ChevronRight size={16} /></button>
          </div>
        </div>
      )}

      {/* Step 2: What Happened */}
      {step === 2 && (
        <div className="ff-card" data-testid="step-2">
          <h2>Step 2: What Happened?</h2>
          <Field label="Subject / Issue" name="subject" value={form.subject} onChange={handleChange} placeholder="e.g., Directed to work on energized panel" required />
          <Field label="What Happened" name="what_happened" value={form.what_happened} onChange={handleChange} placeholder="Describe the event in your own words..." multiline required />
          <Field label="Incident Date" name="incident_date" value={form.incident_date} onChange={handleChange} placeholder="YYYY-MM-DD or approximate" />
          <Field label="Meeting Date (if scheduled)" name="meeting_date" value={form.meeting_date} onChange={handleChange} placeholder="Upcoming meeting date" />
          <div className="ff-nav">
            <button className="ff-prev" onClick={() => setStep(1)}><ChevronLeft size={16} /> Back</button>
            <button className="ff-next" onClick={() => setStep(3)} data-testid="next-btn">Next <ChevronRight size={16} /></button>
          </div>
        </div>
      )}

      {/* Step 3: Details */}
      {step === 3 && (
        <div className="ff-card" data-testid="step-3">
          <h2>Step 3: Instructions &amp; Response</h2>
          <Field label="What instruction were you given?" name="instruction_given" value={form.instruction_given} onChange={handleChange} placeholder="Exact words if possible" multiline />
          <Field label="How did you respond?" name="employee_response" value={form.employee_response} onChange={handleChange} placeholder="What you did or said" multiline />
          <div className="ff-nav">
            <button className="ff-prev" onClick={() => setStep(2)}><ChevronLeft size={16} /> Back</button>
            <button className="ff-next" onClick={() => setStep(4)} data-testid="next-btn">Next <ChevronRight size={16} /></button>
          </div>
        </div>
      )}

      {/* Step 4: Context */}
      {step === 4 && (
        <div className="ff-card" data-testid="step-4">
          <h2>Step 4: Supporting Context</h2>
          <div className="ff-grid">
            <Field label="Witnesses" name="witnesses" value={form.witnesses} onChange={handleChange} placeholder="Names of anyone who saw or heard" />
            <Field label="Documents / Evidence Available" name="documents_available" value={form.documents_available} onChange={handleChange} placeholder="Emails, texts, photos, records" />
          </div>
          <Field label="Safety Concerns" name="safety_concerns" value={form.safety_concerns} onChange={handleChange} placeholder="Any safety issue involved?" multiline />
          <Field label="Contract / Fairness Concerns" name="contract_concerns" value={form.contract_concerns} onChange={handleChange} placeholder="Any contract violation or unfair treatment?" multiline />
          <Field label="Prior Related Events" name="prior_related_events" value={form.prior_related_events} onChange={handleChange} placeholder="Earlier conversations or similar incidents" multiline />
          <Field label="Notes" name="notes" value={form.notes} onChange={handleChange} placeholder="Anything else" multiline />
          <div className="ff-nav">
            <button className="ff-prev" onClick={() => setStep(3)}><ChevronLeft size={16} /> Back</button>
            <button className="ff-next" onClick={() => setStep(5)} data-testid="next-btn">Review <ChevronRight size={16} /></button>
          </div>
        </div>
      )}

      {/* Step 5: Review & Generate */}
      {step === 5 && (
        <div className="ff-card" data-testid="step-5-review">
          <h2>Review &amp; Generate</h2>
          <p style={{ color: "var(--text-secondary)", marginBottom: 16 }}>Check your entries, then generate your Fact Finder analysis or view likely questions.</p>

          <div className="ff-review-grid">
            {Object.entries(form).filter(([, v]) => v?.trim()).map(([k, v]) => (
              <div key={k} className="ff-review-item">
                <span className="ff-review-label">{k.replace(/_/g, " ")}</span>
                <span className="ff-review-value">{v}</span>
              </div>
            ))}
          </div>

          {saveMessage && <div className="ff-save-msg">{saveMessage}</div>}

          <div className="ff-actions" data-testid="review-actions">
            <button className="ff-prev" onClick={() => setStep(4)}><ChevronLeft size={16} /> Back</button>
            <div className="ff-actions-right">
              <button onClick={saveDraft} className="ff-btn-save" data-testid="save-draft-btn">
                <Save size={14} /> Save Draft
              </button>
              <button onClick={generateQuestionsOnly} disabled={questionLoading} className="ff-btn-questions" data-testid="generate-questions-btn">
                {questionLoading ? <><Loader2 size={14} className="ff-spin" /> Generating...</> : <><HelpCircle size={14} /> What They May Ask You</>}
              </button>
              <button onClick={runAnalysis} disabled={loading} className="ff-btn-analyze" data-testid="generate-analysis-btn">
                {loading ? <><Loader2 size={14} className="ff-spin" /> Analyzing...</> : "Generate Fact Finder"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 6: Results */}
      {step === 6 && result && (
        <div data-testid="step-6-results">
          <div className="ff-results-header">
            <div className="ff-results-title-row">
              <h2>Fact Finder Results</h2>
              <LinkedCaseChip
                sourceType="fact_finder"
                sourceId={currentDraftId}
                refreshKey={linkedRefreshKey}
              />
            </div>
            <div className="ff-results-actions">
              <button onClick={() => setStep(5)} className="ff-prev" data-testid="back-to-review"><ChevronLeft size={16} /> Edit</button>
              <button onClick={saveDraft} className="ff-btn-save" data-testid="save-results-btn"><Save size={14} /> Save</button>
              <button onClick={exportPdf} className="ff-btn-pdf" data-testid="export-pdf-btn"><Download size={14} /> Export PDF</button>
              {currentDraftId && (
                <button onClick={() => navigate(`/grievance-builder?from_ff=${currentDraftId}`)} className="ff-btn-grievance" data-testid="start-grievance-btn"><ClipboardList size={14} /> Grievance Prep</button>
              )}
              {currentDraftId && (
                <button
                  onClick={() => setShowCaseLockerModal(true)}
                  className="ff-btn-case"
                  data-testid="save-to-case-locker-btn"
                >
                  <FolderLock size={14} /> Save to Case Locker
                </button>
              )}
            </div>
          </div>

          {saveMessage && <div className="ff-save-msg">{saveMessage}</div>}

          {result.incident_summary && (
            <ResultBlock title="Incident Summary" icon={FileText}>
              <p>{result.incident_summary}</p>
            </ResultBlock>
          )}

          {result.timeline?.length > 0 && (
            <ResultBlock title="Timeline" icon={List}>
              <ul>{result.timeline.map((t, i) => <li key={i}>{t}</li>)}</ul>
            </ResultBlock>
          )}

          {result.red_flags?.length > 0 && (
            <ResultBlock title="Red Flags" icon={AlertTriangle} accentColor="var(--color-danger)">
              <ul>{result.red_flags.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </ResultBlock>
          )}

          {result.strategy?.length > 0 && (
            <ResultBlock title="Strategy" icon={Shield} accentColor="#22d3ee">
              <ul>{result.strategy.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </ResultBlock>
          )}

          {result.statement_draft && (
            <ResultBlock title="Statement Draft" icon={MessageSquare}>
              <p className="ff-statement">{result.statement_draft}</p>
            </ResultBlock>
          )}

          {result.meeting_prep?.length > 0 && (
            <ResultBlock title="Meeting Prep" icon={FileText} accentColor="#f59e0b">
              <ul>{result.meeting_prep.map((m, i) => <li key={i}>{m}</li>)}</ul>
            </ResultBlock>
          )}

          {result.likely_questions?.length > 0 && (
            <ResultBlock title="What They May Ask You" icon={HelpCircle} accentColor="#a78bfa">
              <ol className="ff-questions-list">
                {result.likely_questions.map((q, i) => <li key={i}>{q}</li>)}
              </ol>
            </ResultBlock>
          )}
        </div>
      )}

      {/* Save to Case Locker modal */}
      <SaveToCaseLockerModal
        open={showCaseLockerModal}
        onClose={() => setShowCaseLockerModal(false)}
        onSaved={() => setLinkedRefreshKey(k => k + 1)}
        sourceType="fact_finder"
        sourceId={currentDraftId}
        prefill={{
          title:
            (form.subject && `Fact Finder: ${form.subject.slice(0, 60)}`) ||
            `Fact Finder – ${form.incident_date || new Date().toISOString().slice(0, 10)}`,
          summary:
            (result && result.incident_summary) ||
            form.what_happened ||
            "",
          issue_type: inferFactFinderIssueType(form),
          incident_date: form.incident_date || "",
          people_involved: buildFactFinderPeople(form),
          witnesses: splitCsv(form.witnesses),
          timeline_description:
            `Case created from Fact Finder draft ${currentDraftId}${
              form.subject ? ` — ${form.subject}` : ""
            }`,
        }}
      />
    </div>
  );
}

function splitCsv(val) {
  return (val || "")
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function buildFactFinderPeople(form) {
  const people = [];
  if (form.supervisor_name?.trim()) {
    people.push({ name: form.supervisor_name.trim(), role: "Supervisor", notes: "" });
  }
  splitCsv(form.other_people).forEach((p) =>
    people.push({ name: p, role: "", notes: "" })
  );
  return people;
}

function inferFactFinderIssueType(form) {
  const haystack = [
    form.subject, form.what_happened, form.instruction_given,
    form.safety_concerns, form.contract_concerns,
  ].join(" ").toLowerCase();
  if (/ppe|safety|arc flash|hazard|unsafe|lockout|tagout/.test(haystack)) return "safety_concern";
  if (/weingarten|steward|investigat|hr meeting/.test(haystack)) return "weingarten_violation";
  if (/overtime|mandatory|ot list|bypass/.test(haystack)) return "overtime_dispute";
  if (/shift|schedule|days off|notice/.test(haystack)) return "schedule_dispute";
  if (/writ(ten|e) up|discipline|suspended|termination|warning/.test(haystack)) return "discipline_investigation";
  if (/senior/.test(haystack)) return "seniority_dispute";
  if (/pay|wage|rate|differential/.test(haystack)) return "pay_grievance";
  if (/harass|bully|retaliat/.test(haystack)) return "harassment";
  return "other";
}

const cssText = `
.ff-page { max-width: 820px; margin: 0 auto; padding: 20px 16px; min-height: 100vh; font-family: 'Inter', -apple-system, sans-serif; background: var(--surface-base); color: var(--text-primary); }
.ff-pg-header { margin-bottom: 20px; }
.ff-back { background: none; border: 1px solid var(--border-default); color: var(--text-secondary); padding: 6px 14px; border-radius: 8px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; font-size: 13px; transition: all 0.15s; margin-bottom: 12px; }
.ff-back:hover { border-color: var(--border-hover); color: var(--text-primary); }
.ff-badge { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 600; background: rgba(99,102,241,0.12); color: #818cf8; border: 1px solid rgba(99,102,241,0.2); text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 8px; }
.ff-pg-header h1 { font-size: 26px; font-weight: 700; margin: 4px 0; }
.ff-pg-header p { color: var(--text-secondary); font-size: 14px; margin: 0; }

/* Step bar */
.ff-step-bar { display: flex; gap: 4px; margin-bottom: 20px; overflow-x: auto; }
.ff-step { display: flex; align-items: center; gap: 6px; padding: 8px 14px; border-radius: 10px; background: var(--surface-card); border: 1px solid var(--border-default); font-size: 12px; color: var(--text-muted); flex-shrink: 0; transition: all 0.15s; }
.ff-step.active { color: var(--text-secondary); border-color: var(--border-hover); }
.ff-step.current { background: var(--color-accent-soft); color: var(--color-accent); border-color: var(--color-accent-border); }
.ff-step-num { width: 20px; height: 20px; border-radius: 50%; background: var(--surface-card); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 11px; }
.ff-step.current .ff-step-num { background: var(--color-accent); color: var(--surface-base); }
.ff-step-label { font-weight: 600; }

/* Card */
.ff-card { background: var(--surface-card); border: 1px solid var(--border-default); border-radius: 16px; padding: 24px; margin-bottom: 16px; }
.ff-card h2 { font-size: 18px; font-weight: 700; margin: 0 0 16px; }

/* Field */
.ff-field { margin-bottom: 14px; }
.ff-field label { display: block; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-muted); margin-bottom: 6px; }
.ff-req { color: var(--color-danger); margin-left: 2px; }
.ff-field input, .ff-field textarea {
  width: 100%; padding: 10px 12px; border-radius: 10px;
  background: var(--surface-base); border: 1px solid var(--border-default);
  color: var(--text-primary); font-size: 14px; font-family: inherit;
  transition: border-color 0.15s;
}
.ff-field input:focus, .ff-field textarea:focus { outline: none; border-color: var(--color-accent); }
.ff-field textarea { resize: vertical; min-height: 80px; }

.ff-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 16px; }
@media (max-width: 640px) { .ff-grid { grid-template-columns: 1fr; } }

/* Nav */
.ff-nav { display: flex; justify-content: space-between; align-items: center; margin-top: 20px; }
.ff-prev, .ff-next { display: inline-flex; align-items: center; gap: 4px; padding: 10px 18px; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.15s; }
.ff-prev { background: none; border: 1px solid var(--border-default); color: var(--text-secondary); }
.ff-prev:hover { border-color: var(--border-hover); color: var(--text-primary); }
.ff-next { background: var(--color-accent); border: none; color: var(--surface-base); }
.ff-next:hover { filter: brightness(1.1); }

/* Review grid */
.ff-review-grid { display: grid; grid-template-columns: 1fr; gap: 8px; margin-bottom: 20px; }
.ff-review-item { background: var(--surface-base); border: 1px solid var(--border-default); border-radius: 10px; padding: 10px 14px; }
.ff-review-label { display: block; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--text-muted); margin-bottom: 3px; }
.ff-review-value { font-size: 14px; color: var(--text-primary); line-height: 1.5; }

/* Actions */
.ff-actions { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; margin-top: 16px; }
.ff-actions-right { display: flex; gap: 8px; flex-wrap: wrap; }
.ff-btn-save { display: inline-flex; align-items: center; gap: 6px; padding: 10px 16px; border-radius: 10px; font-size: 13px; font-weight: 600; cursor: pointer; background: var(--surface-card); border: 1px solid var(--border-default); color: var(--text-secondary); transition: all 0.15s; }
.ff-btn-save:hover { border-color: var(--border-hover); color: var(--text-primary); }
.ff-btn-pdf { display: inline-flex; align-items: center; gap: 6px; padding: 10px 16px; border-radius: 10px; font-size: 13px; font-weight: 600; cursor: pointer; background: rgba(34,197,94,0.1); border: 1px solid rgba(34,197,94,0.25); color: #22c55e; transition: all 0.15s; }
.ff-btn-pdf:hover { background: rgba(34,197,94,0.2); }
.ff-btn-grievance { display: inline-flex; align-items: center; gap: 6px; padding: 10px 16px; border-radius: 10px; font-size: 13px; font-weight: 600; cursor: pointer; background: rgba(245,158,11,0.1); border: 1px solid rgba(245,158,11,0.25); color: #fbbf24; transition: all 0.15s; }
.ff-btn-grievance:hover { background: rgba(245,158,11,0.2); }
.ff-btn-case { display: inline-flex; align-items: center; gap: 6px; padding: 10px 16px; border-radius: 10px; font-size: 13px; font-weight: 600; cursor: pointer; background: rgba(139,92,246,0.12); border: 1px solid rgba(139,92,246,0.3); color: #c4b5fd; transition: all 0.15s; }
.ff-btn-case:hover { background: rgba(139,92,246,0.22); }
.ff-btn-questions { display: inline-flex; align-items: center; gap: 6px; padding: 10px 16px; border-radius: 10px; font-size: 13px; font-weight: 600; cursor: pointer; background: rgba(167,139,250,0.12); border: 1px solid rgba(167,139,250,0.25); color: #a78bfa; transition: all 0.15s; }
.ff-btn-questions:hover { background: rgba(167,139,250,0.2); }
.ff-btn-questions:disabled { opacity: 0.5; cursor: not-allowed; }
.ff-btn-analyze { display: inline-flex; align-items: center; gap: 6px; padding: 10px 20px; border-radius: 10px; font-size: 14px; font-weight: 700; cursor: pointer; background: var(--color-accent); border: none; color: var(--surface-base); transition: all 0.15s; }
.ff-btn-analyze:hover { filter: brightness(1.1); }
.ff-btn-analyze:disabled { opacity: 0.5; cursor: not-allowed; }
.ff-save-msg { padding: 8px 14px; border-radius: 8px; background: rgba(34,197,94,0.1); border: 1px solid rgba(34,197,94,0.2); color: var(--color-success); font-size: 13px; margin: 10px 0; }

/* Spinner */
.ff-spin { animation: ffSpin 1s linear infinite; }
@keyframes ffSpin { to { transform: rotate(360deg); } }

/* Results */
.ff-results-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; flex-wrap: wrap; gap: 10px; }
.ff-results-title-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.ff-results-title-row h2 { margin: 0; }
.ff-results-header h2 { font-size: 22px; font-weight: 700; margin: 0; }
.ff-results-actions { display: flex; gap: 8px; }
.ff-result-block { background: var(--surface-card); border: 1px solid var(--border-default); border-radius: 14px; padding: 18px; margin-bottom: 12px; }
.ff-rb-header { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
.ff-rb-header h3 { font-size: 15px; font-weight: 700; margin: 0; }
.ff-rb-body { font-size: 14px; line-height: 1.7; color: var(--text-secondary); }
.ff-rb-body p { margin: 0; }
.ff-rb-body ul, .ff-rb-body ol { margin: 0; padding-left: 20px; }
.ff-rb-body li { margin-bottom: 6px; }
.ff-rb-body li::marker { color: var(--text-muted); }
.ff-statement { background: var(--surface-base); border: 1px solid var(--border-default); border-radius: 10px; padding: 14px; font-style: italic; line-height: 1.7; }
.ff-questions-list { counter-reset: q; list-style: none; padding-left: 0; }
.ff-questions-list li { counter-increment: q; padding: 8px 0 8px 32px; position: relative; border-bottom: 1px solid var(--border-default); }
.ff-questions-list li:last-child { border-bottom: none; }
.ff-questions-list li::before { content: counter(q); position: absolute; left: 0; top: 8px; width: 22px; height: 22px; border-radius: 50%; background: rgba(167,139,250,0.12); color: #a78bfa; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; }

@media (max-width: 640px) {
  .ff-actions { flex-direction: column; align-items: stretch; }
  .ff-actions-right { flex-direction: column; }
  .ff-btn-analyze, .ff-btn-questions, .ff-btn-save { width: 100%; justify-content: center; }
  .ff-results-header { flex-direction: column; align-items: flex-start; }
  .ff-step-bar { gap: 2px; }
  .ff-step { padding: 6px 10px; font-size: 11px; }
}
`;
